import React, { useCallback, useEffect, useState, FormEvent } from 'react';
import { durationText } from 'reusable/durationText';
import axiosInstance from 'config/axiosInstance';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { businessDateTimeToIsoKeeping, businessMonth, businessRangeQuery, formatBusiness, monthBounds } from 'config/time';
import { PAGE_SIZE } from 'config/constant';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import { IconEdit } from '@tabler/icons-react';
import EditDialog from 'reusable/Dialog/editDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
} from 'reusable/Dialog/dialogInterface';
import { correctionReason, editAttendanceFields } from '../../../Attendance/AttendanceField';
import { useTranslation } from 'react-i18next';
import Attendance from 'components/Attendance';
import DeviceTimeZone, { deviceTimeZoneOf, isAdminEnteredEnd } from 'reusable/DeviceTimeZone';
import { useSaveError } from 'reusable/useSaveError';
import { pageToRecover } from 'reusable/pageRecovery';
import { useMonthPage } from 'reusable/useMonthPage';


interface DriverWorkingAttendanceTableProps {
  id?: string;
  /** A36（2026-11-03 和田様の UAT）。ドライバー詳細の上部の月の切り替えで選んだ月（`YYYY-MM`）。既定は今の月。 */
  month?: string;
}

interface DriverWorkingAttendance {
  id: string;
  check_in: string;
  check_out: string;
}

/** 入力欄の値を文字列として扱う。未入力はnull。 */
const asText = (value: string | number | null | undefined): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

const DriverWorkingAttendanceTable: React.FC<
  DriverWorkingAttendanceTableProps
> = ({ id, month = businessMonth() }) => {
  const { t, i18n } = useTranslation('driver');
  // EV-113。勤怠の編集欄・ボタンの文は attendance の翻訳にある（driver の t ではキーのまま出ていた）。
  const { t: tAttendance } = useTranslation('attendance');
  // 既定は新しい順（サーバーの並び順）。もう一方は記録の時刻の古い順。
  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.oldest', { ns: 'common' }), value: 'check_in' },
  ];
  const [currentPage, setCurrentPage] = useMonthPage(month);
  const [sortValue, setSortValue] = useState('');
  const [count, setCount] = useState(0);
  const [driverWorkingAttendance, setDriverWorkingAttendance] = useState<
    DriverWorkingAttendance[]
  >([]);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { showSaveError, saveErrorPopup } = useSaveError();
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentAttendanceId, setCurrentAttendanceId] = useState<string | null>(
    null
  );

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const formatDate = (datetime: string) => {
    // EV-116。端末の時間帯ではなく、業務の時間帯（日本時間）で出す（絞り込みの業務日と揃える）。
    return formatBusiness(
      datetime,
      i18n.language === 'jp' ? 'yyyy/MM/dd | HH:mm' : 'dd MMM yyyy | HH:mm',
      '-'
    );
  };

  const calculateDuration = (checkIn: string, checkOut: string) => {
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);

    if (checkOutDate.getTime() > checkInDate.getTime()) {
      const durationMs = checkOutDate.getTime() - checkInDate.getTime();
      const durationMins = Math.floor(durationMs / 60000);
      const hours = Math.floor(durationMins / 60);
      const minutes = durationMins % 60;

      // EV-312。英語は時・分の単複を出し分ける（common の ui.duration-hm）。数でなければ「-」。
      return durationText(t, 'ui.duration-hm', { hours, minutes }, { ns: 'common' });
    } else {
      return '-';
    }
  };

  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      // A36。選んだ月の業務日の範囲（出勤の時刻）。
      const { start, end } = monthBounds(month);
      let apiUrl = '/v1/attendances/?';
      apiUrl += businessRangeQuery('check_in', start, end);
      apiUrl += `order_by=${sortValue}&page=${currentPage}&page_size=${PAGE_SIZE}&user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, PAGE_SIZE, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      if (!isCurrent()) return;
      setFetchError(null);
      setDriverWorkingAttendance(response.data.results);
      setCount(response.data.count);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, PAGE_SIZE, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch data', error);
      setDriverWorkingAttendance([]);
      setFetchError(error);
    }
  }, [id, currentPage, sortValue, month, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    console.log('Opening dialog with fields:', fields); // Debugging line
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
  };

  const closeDialog = () => {
    setIsEditDialogOpen(false);
    setCurrentAttendanceId(null);
  };

  const handleDialog = async (
    dialogType: DialogType,
    attendance: DriverWorkingAttendance
  ) => {
    console.log('DialogType:', dialogType, 'Attendance:', attendance); // Debugging line
    if (dialogType === DialogType.Edit) {
      try {
        setCurrentAttendanceId(attendance.id);
        openDialog(
          t('view.working-attendance.edit-title'),
          '',
          editAttendanceFields(tAttendance, attendance),
          DialogType.Edit
        );
      } catch (error) {
        console.error('Failed to fetch attendance data', error);
      }
    }
  };

  const handleEditAttendance = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    // ADM-04。画面の入力は日本時間。`Z` を付けて結合するとUTCとして解釈され、
    // 保存値が9時間ずれる。未退勤（時刻が空）の場合は項目ごと送らない。
    // 変えていない項目は元の値（秒・ミリ秒を含む）をそのまま送る。
    const original = driverWorkingAttendance.find(
      (attendance) => String(attendance.id) === String(updatedData.id)
    );
    const checkInDateTime = businessDateTimeToIsoKeeping(
      asText(updatedData.check_in_date),
      asText(updatedData.check_in_time),
      original?.check_in
    );
    const checkOutDateTime = businessDateTimeToIsoKeeping(
      asText(updatedData.check_out_date),
      asText(updatedData.check_out_time),
      original?.check_out
    );

    const formattedUpdatedData: Record<string, string> = {};
    if (checkInDateTime) formattedUpdatedData.check_in = checkInDateTime;
    if (checkOutDateTime) formattedUpdatedData.check_out = checkOutDateTime;
    // BE-09-AC5（RA-AC3）。修正の理由（サーバーで必須。修正の前後と一緒に記録される）。
    formattedUpdatedData.reason = correctionReason(updatedData);

    try {
      if (updatedData) {
        const response = await axiosInstance.patch(
          `/v1/attendances/${updatedData.id}/`,
          formattedUpdatedData
        );

        setDriverWorkingAttendance((prevData) =>
          prevData.map((attendance) =>
            attendance.id === updatedData.id ? response.data : attendance
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update attendance:', error);
      // 訂正が拒否された理由（退勤が出勤より前など）を表示する。
      // ADM-15。ダイアログは開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(error);
    }
  };
console.log(driverWorkingAttendance);
  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
      </div>

      <div>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1 text-center">
            {t('view.working-attendance.check-in')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-attendance.check-out')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-attendance.duration')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('time-zone.device', { ns: 'common' })}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-attendance.action')}
          </h6>
        </div>
        <div>
          {fetchError ? <FetchFailed error={fetchError} onRetry={fetchData} /> : null}
          {driverWorkingAttendance.map((attendance, index) => (
            <div
              key={index}
              className="grid grid-cols-5 gap-4 px-4 py-3 border-b items-center"
            >
              <div className="flex justify-center">
                {formatDate(attendance.check_in)}
              </div>
              <div className="flex justify-center">
                {formatDate(attendance.check_out)}
              </div>
              <div className="flex justify-center">
                {calculateDuration(attendance.check_in, attendance.check_out)}
              </div>
              {/* EV-116 段階3。出勤・退勤の時点の端末の時間帯（同じ列に2つ。既存の見出しの文言で区別）。 */}
              <div className="flex flex-col items-center text-center">
                <DeviceTimeZone
                  {...deviceTimeZoneOf(attendance)}
                  at={attendance.check_in}
                  label={t('view.working-attendance.check-in')}
                />
                {attendance.check_out && (
                  <DeviceTimeZone
                    {...deviceTimeZoneOf(attendance, 'check_out_')} adminEntered={isAdminEnteredEnd(attendance, 'check_out_')}
                    at={attendance.check_out}
                    label={t('view.working-attendance.check-out')}
                  />
                )}
              </div>
              <div className="flex justify-center">
                <button
                  type="button"
                  className="inline-flex items-center"
                  aria-label={t('ui.edit', { ns: 'common' })}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDialog(DialogType.Edit, attendance);
                  }}
                >
                  <IconEdit
                    size={20}
                    stroke={1.5}
                    className="cursor-pointer"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <EditDialog
        isOpen={isEditDialogOpen}
        onClose={closeDialog}
        handleSubmit={(e: any, updatedData: any) =>
          handleEditAttendance(e, {
            ...updatedData,
            id: currentAttendanceId ?? '',
          })
        }
        confirmLabel={tAttendance('edit.save')}
        cancelLabel={tAttendance('edit.cancel')}
        title={dialogProps.title}
        subtitle={dialogProps.subtitle ?? ''}
        fields={dialogProps.fields}
        attendanceId={currentAttendanceId ?? ''}
      />

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
      {saveErrorPopup}
    </section>
  );
};

export default DriverWorkingAttendanceTable;
